import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  Divider,
  Drawer,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaymentIcon from '@mui/icons-material/Payment';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import moment from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { HotelDateTimePicker as DateTimePicker } from '../../../shared/components/forms/HotelDatePicker';
import {
  createPosOrderPayment,
  deletePosOrderPayment,
  getPosOrderPayments,
  updatePosOrderPayment,
} from '../api';
import {
  BOOKING_DRAWER_ACCENT,
  BOOKING_DRAWER_WIDTH_SX,
  getBookingDrawerFooterSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerInputBg,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPaperSx,
  getBookingDrawerPrimaryButtonSx,
  getBookingDrawerTheme,
} from '../../../utils/bookingDrawerLayout';
import {
  getDrawerDatePickerSlotProps,
  getDrawerRootSx,
} from '../../../utils/drawerOverlayProps';
import { formatHotelDateTime, toPaymentProcessedAtIso } from '../../../utils/dateUtils';
import { round2 } from '../../../utils/bookingPaymentSummary';
import BookingPaymentBreakdown, { BookingGstModeChip } from '../../../components/booking/BookingPaymentBreakdown';
import BookingInvoicePreviewDialog from '../../../components/booking/BookingInvoicePreviewDialog';
import {
  downloadPosOrderInvoice,
  fetchPosOrderDraftInvoiceDefaults,
  fetchPosOrderDraftInvoicePdfBlob,
  fetchPosOrderInvoicePdfBlob,
} from '../api';

const ACCENT = BOOKING_DRAWER_ACCENT;
const DELETE_ACTION_COLOR = '#f44336';
const DELETE_CONFIRM_WORD = 'confirm';

const PAYMENT_METHOD_OPTIONS = [
  'Online',
  'Cash',
  'Card',
  'Bank Transfer',
  'Company Settlement',
  'UPI',
  'Credit Sale',
  'Cheque',
  'Payment Link',
];

const PAYMENT_STATUS_OPTIONS = [
  { value: 'captured', label: 'Captured' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
];

const RESTAURANT_PAYMENT_ACCESS_MESSAGE =
  'Payment collection for this restaurant order is managed by the restaurant team. Please contact the restaurant to settle this bill.';

const isRestaurantPaymentAccessDenied = (err) => {
  if (Number(err?.statusCode) === 403) return true;
  const raw = String(err?.userMessage || err?.message || '').toLowerCase();
  return raw.includes('access denied') || raw.includes('do not have permission');
};

const restaurantPaymentErrorMessage = (err, fallback) => {
  if (isRestaurantPaymentAccessDenied(err)) return RESTAURANT_PAYMENT_ACCESS_MESSAGE;
  return err?.userMessage || fallback;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const parseAmount = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Always show money inputs with exactly 2 decimal places. */
const formatAmountInput = (value) => {
  if (value === '' || value == null) return '';
  const num = typeof value === 'number' ? value : Number.parseFloat(String(value).replace(/,/g, ''));
  if (!Number.isFinite(num) || num <= 0) return '';
  return round2(num).toFixed(2);
};

const toMomentValue = (value) => {
  if (!value) return moment();
  const parsed = moment(value);
  return parsed.isValid() ? parsed : moment();
};

const getPaymentStatusColors = (status, isDark) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'captured') {
    return {
      bg: isDark ? 'rgba(16, 185, 129, 0.18)' : 'rgba(16, 185, 129, 0.12)',
      color: isDark ? '#6ee7b7' : '#047857',
      label: 'Captured',
    };
  }
  if (normalized === 'failed') {
    return {
      bg: isDark ? 'rgba(239, 68, 68, 0.18)' : 'rgba(239, 68, 68, 0.12)',
      color: isDark ? '#fca5a5' : '#b91c1c',
      label: 'Failed',
    };
  }
  if (normalized === 'refunded') {
    return {
      bg: isDark ? 'rgba(245, 158, 11, 0.18)' : 'rgba(245, 158, 11, 0.12)',
      color: isDark ? '#fcd34d' : '#b45309',
      label: 'Refunded',
    };
  }
  return {
    bg: isDark ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.12)',
    color: isDark ? '#cbd5e1' : '#475569',
    label: status || 'Unknown',
  };
};

function getDrawerSectionPaperSx(isDark, drawerTheme, extra = {}) {
  return {
    p: 1.5,
    borderRadius: '12px',
    backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
    border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
    ...extra,
  };
}

function DeletePaymentConfirmDialog({
  open,
  payment,
  isDark,
  drawerTheme,
  modalContainer,
  confirmText,
  onConfirmTextChange,
  onClose,
  onConfirm,
  deleting,
}) {
  const confirmInputRef = useRef(null);
  const confirmValid = confirmText.trim().toLowerCase() === DELETE_CONFIRM_WORD;
  const statusColors = getPaymentStatusColors(payment?.status, isDark);
  const method = String(payment?.payment_method || 'Cash').trim() || 'Cash';
  const collectedLabel = payment?.collected_at
    ? formatHotelDateTime(payment.collected_at)
    : '—';

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => {
      confirmInputRef.current?.focus();
    }, 120);
    return () => window.clearTimeout(timer);
  }, [open]);

  const resolveContainer = () => {
    if (typeof modalContainer === 'function') return modalContainer() || undefined;
    return modalContainer || undefined;
  };

  return (
    <Dialog
      open={open}
      onClose={() => !deleting && onClose?.()}
      container={resolveContainer}
      disableScrollLock
      sx={{
        position: 'absolute',
        zIndex: 1600,
        '& .MuiBackdrop-root': {
          position: 'absolute',
        },
        '& .MuiDialog-container': {
          position: 'absolute',
          inset: 0,
        },
      }}
      BackdropProps={{
        sx: {
          position: 'absolute',
          backgroundColor: isDark ? 'rgba(0, 0, 0, 0.68)' : 'rgba(0, 0, 0, 0.45)',
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: '14px',
          overflow: 'hidden',
          backgroundColor: isDark ? drawerTheme.surface : '#fff',
          border: `1px solid ${alpha(DELETE_ACTION_COLOR, isDark ? 0.28 : 0.16)}`,
          boxShadow: isDark
            ? `0 16px 36px rgba(0, 0, 0, 0.5), 0 0 0 1px ${alpha(DELETE_ACTION_COLOR, 0.12)}`
            : `0 14px 32px rgba(211, 47, 47, 0.18), 0 0 0 1px ${alpha(DELETE_ACTION_COLOR, 0.08)}`,
          width: '100%',
          maxWidth: 400,
          m: 1.5,
          position: 'relative',
          zIndex: 1,
        },
      }}
    >
      <Box
        sx={{
          px: 1.35,
          py: 1.1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          background: isDark
            ? `linear-gradient(120deg, ${alpha(DELETE_ACTION_COLOR, 0.32)} 0%, ${alpha('#ff7043', 0.14)} 45%, ${alpha(ACCENT, 0.06)} 100%)`
            : `linear-gradient(120deg, ${alpha(DELETE_ACTION_COLOR, 0.16)} 0%, ${alpha('#ff8a65', 0.1)} 50%, ${alpha(ACCENT, 0.05)} 100%)`,
          borderBottom: `1px solid ${alpha(DELETE_ACTION_COLOR, isDark ? 0.28 : 0.14)}`,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            background: `linear-gradient(145deg, ${alpha(DELETE_ACTION_COLOR, 0.95)} 0%, #c62828 100%)`,
            boxShadow: `0 6px 14px ${alpha(DELETE_ACTION_COLOR, 0.4)}`,
          }}
        >
          <DeleteForeverIcon sx={{ color: '#fff', fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, color: drawerTheme.text, lineHeight: 1.15, fontSize: '0.9rem' }}
          >
            Delete payment?
          </Typography>
          <Typography variant="caption" sx={{ color: drawerTheme.subtleText, fontSize: '0.68rem' }}>
            Permanent · order balance recalculates
          </Typography>
        </Box>
        <IconButton
          size="small"
          disabled={deleting}
          onClick={onClose}
          sx={{
            width: 28,
            height: 28,
            color: drawerTheme.subtleText,
            backgroundColor: isDark ? alpha('#000', 0.22) : alpha('#fff', 0.65),
            border: `1px solid ${alpha('#fff', isDark ? 0.08 : 0.5)}`,
          }}
        >
          <CloseIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: 1.5, py: 1.35 }}>
        <Paper
          elevation={0}
          sx={{
            p: 1.15,
            mb: 1.1,
            borderRadius: '10px',
            background: isDark
              ? `linear-gradient(135deg, ${alpha(DELETE_ACTION_COLOR, 0.12)} 0%, ${alpha('#000', 0.2)} 100%)`
              : `linear-gradient(135deg, ${alpha(DELETE_ACTION_COLOR, 0.06)} 0%, ${alpha('#fff', 0.9)} 100%)`,
            border: `1px solid ${alpha(DELETE_ACTION_COLOR, isDark ? 0.22 : 0.14)}`,
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '1.05rem',
                  color: isDark ? '#ff8a80' : '#c62828',
                  lineHeight: 1.15,
                }}
              >
                {formatCurrency(payment?.amount)}
              </Typography>
              <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.65, flexWrap: 'wrap' }} useFlexGap>
                <Chip
                  size="small"
                  label={statusColors.label}
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    backgroundColor: statusColors.bg,
                    color: statusColors.color,
                  }}
                />
                <Chip
                  size="small"
                  label={method}
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    backgroundColor: isDark ? alpha(ACCENT, 0.12) : alpha(ACCENT, 0.1),
                    color: ACCENT,
                  }}
                />
              </Stack>
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.7, color: drawerTheme.subtleText, fontSize: '0.68rem' }}
              >
                {collectedLabel}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Stack direction="row" spacing={0.65} alignItems="flex-start" sx={{ mb: 1 }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 16, color: '#ff9800', mt: 0.1 }} />
          <Typography variant="caption" sx={{ color: drawerTheme.subtleText, lineHeight: 1.4, fontSize: '0.72rem' }}>
            This cannot be undone. Type{' '}
            <Box
              component="span"
              sx={{
                px: 0.5,
                py: 0.05,
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '0.7rem',
                fontWeight: 700,
                color: confirmValid ? DELETE_ACTION_COLOR : drawerTheme.text,
                backgroundColor: alpha(DELETE_ACTION_COLOR, isDark ? 0.14 : 0.08),
                border: `1px solid ${confirmValid ? alpha(DELETE_ACTION_COLOR, 0.5) : (isDark ? alpha('#fff', 0.1) : alpha('#000', 0.1))}`,
              }}
            >
              {DELETE_CONFIRM_WORD}
            </Box>
            {' '}below to enable delete.
          </Typography>
        </Stack>

        <TextField
          fullWidth
          size="small"
          placeholder={`Type ${DELETE_CONFIRM_WORD}`}
          value={confirmText}
          onChange={(e) => onConfirmTextChange?.(e.target.value)}
          inputRef={confirmInputRef}
          error={confirmText.length > 0 && !confirmValid}
          disabled={deleting}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && confirmValid && !deleting) {
              e.preventDefault();
              onConfirm?.();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon
                  sx={{
                    fontSize: 16,
                    color: confirmValid ? DELETE_ACTION_COLOR : drawerTheme.subtleText,
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              fontSize: '0.88rem',
              minHeight: 42,
              backgroundColor: isDark ? alpha('#000', 0.18) : alpha('#000', 0.02),
              '& fieldset': {
                borderColor: isDark ? alpha('#fff', 0.12) : alpha('#000', 0.12),
              },
              '&.Mui-focused fieldset': {
                borderColor: confirmValid
                  ? DELETE_ACTION_COLOR
                  : (isDark ? alpha('#fff', 0.28) : alpha('#000', 0.28)),
              },
            },
          }}
        />

        <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={deleting}
            sx={{
              flex: 1,
              py: 0.85,
              textTransform: 'none',
              borderRadius: '10px',
              fontWeight: 600,
              fontSize: '0.82rem',
              color: drawerTheme.text,
              borderColor: isDark ? alpha('#fff', 0.16) : alpha('#000', 0.14),
            }}
          >
            Keep Payment
          </Button>
          <Button
            variant="contained"
            disabled={deleting || !confirmValid}
            onClick={onConfirm}
            startIcon={
              deleting ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <DeleteForeverIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              flex: 1,
              py: 0.85,
              textTransform: 'none',
              borderRadius: '10px',
              fontWeight: 800,
              fontSize: '0.82rem',
              color: '#fff',
              background: `linear-gradient(135deg, ${DELETE_ACTION_COLOR} 0%, #c62828 100%)`,
              boxShadow: confirmValid ? `0 8px 18px ${alpha(DELETE_ACTION_COLOR, 0.35)}` : 'none',
              '&:hover': {
                background: `linear-gradient(135deg, #e53935 0%, #b71c1c 100%)`,
              },
              '&.Mui-disabled': {
                background: isDark ? alpha('#fff', 0.08) : '#eceff1',
                color: isDark ? alpha('#fff', 0.35) : '#90a4ae',
                boxShadow: 'none',
              },
            }}
          >
            {deleting ? 'Deleting…' : 'Delete Payment'}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}

function sumCapturedPayments(paymentsList) {
  return round2(
    (paymentsList || []).reduce((sum, payment) => {
      if (String(payment?.status || '').toLowerCase() !== 'captured') return sum;
      return sum + Number(payment?.amount || 0);
    }, 0),
  );
}

function buildRestaurantPaymentSummary(order, liveSummary) {
  const totalAmount = round2(liveSummary?.total_amount ?? order?.total_amount ?? 0);
  const paidAmount = round2(liveSummary?.total_paid ?? order?.partial_paid_amount ?? order?.paid_amount ?? 0);
  // Remaining never negative; paid may exceed total (overpayment / return).
  const remainingAmount = round2(
    liveSummary?.due_amount != null
      ? Math.max(0, Number(liveSummary.due_amount) || 0)
      : order?.due_amount != null
        ? Math.max(0, Number(order.due_amount) || 0)
        : order?.remaining_amount != null
          ? Math.max(0, Number(order.remaining_amount) || 0)
          : Math.max(0, totalAmount - paidAmount),
  );
  const subtotal = round2(order?.subtotal ?? 0);
  const discountAmount = round2(order?.discount_amount ?? 0);
  const gstAmount = round2(order?.tax_amount ?? 0);
  const extraChargesAmount = round2(
    Number(order?.additional_charges ?? 0) ||
      (Number(order?.delivery_charge ?? 0) + Number(order?.container_charge ?? 0)),
  );
  const subtotalBeforeDiscount =
    subtotal > 0
      ? subtotal
      : round2(Math.max(0, totalAmount - gstAmount - extraChargesAmount + discountAmount));
  const subtotalAfterDiscount = round2(Math.max(0, subtotalBeforeDiscount - discountAmount));
  const returnAmount = round2(Math.max(0, paidAmount - totalAmount));

  return {
    currency: 'INR',
    totalAmount,
    displayTotal: totalAmount,
    subtotalBeforeDiscount,
    displaySubtotal: subtotalBeforeDiscount,
    discountAmount,
    discountPercentage: 0,
    subtotalAfterDiscount,
    displayAfterDiscount: subtotalAfterDiscount,
    afterDiscountLabel: 'After Discount',
    gstAmount,
    displayGstAmount: gstAmount,
    gstPercentage: 0,
    gstIncluded: gstAmount > 0,
    gstPrefix: gstAmount > 0 ? '+ ' : '',
    taxInclusive: false,
    inclusiveDisplay: false,
    paidAmount,
    remainingAmount,
    returnAmount,
    extraChargesAmount,
    extraChargesBaseAmount: extraChargesAmount,
    extraChargesGstAmount: 0,
    extraChargesNote: '',
    extraChargesItems: [],
  };
}

function PaymentMethodChipSelector({ value, onChange, isDark, options = PAYMENT_METHOD_OPTIONS }) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: '0.72rem', fontWeight: 600, mb: 1, display: 'block' }}
      >
        Payment Method *
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {options.map((method) => {
          const selected = value === method;
          return (
            <Chip
              key={method}
              label={method}
              onClick={() => onChange(method)}
              sx={{
                cursor: 'pointer',
                height: 32,
                fontSize: '0.75rem',
                fontWeight: selected ? 700 : 600,
                backgroundColor: selected
                  ? ACCENT
                  : (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)'),
                color: selected ? '#000' : ACCENT,
                border: `1px solid ${selected ? ACCENT : (isDark ? 'rgba(76, 225, 182, 0.3)' : 'rgba(76, 225, 182, 0.25)')}`,
                transition: 'all 0.18s ease',
                '&:hover': {
                  backgroundColor: selected
                    ? ACCENT
                    : (isDark ? 'rgba(76, 225, 182, 0.22)' : 'rgba(76, 225, 182, 0.16)'),
                },
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

function PaymentStatusChipSelector({ value, onChange, isDark }) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: '0.72rem', fontWeight: 600, mb: 1, display: 'block' }}
      >
        Status
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {PAYMENT_STATUS_OPTIONS.map((option) => {
          const selected = value === option.value;
          const colors = getPaymentStatusColors(option.value, isDark);
          return (
            <Chip
              key={option.value}
              label={option.label}
              onClick={() => onChange(option.value)}
              sx={{
                cursor: 'pointer',
                height: 28,
                fontSize: '0.72rem',
                fontWeight: selected ? 700 : 600,
                backgroundColor: selected ? colors.bg : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
                color: selected ? colors.color : (isDark ? '#cbd5e1' : '#64748b'),
                border: `1px solid ${selected ? colors.color : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)')}`,
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

const emptyReceiveForm = (dueAmount = '') => ({
  amount: formatAmountInput(dueAmount),
  payment_method: 'Cash',
  note: '',
  collected_at: moment(),
});

export default function PosOrderPaymentDrawer({
  open,
  onClose,
  order,
  booking = null,
  isDark,
  onPaymentsChanged,
}) {
  const drawerTheme = getBookingDrawerTheme(isDark);
  const drawerRootRef = useRef(null);
  const datePickerSlotProps = useMemo(
    () => getDrawerDatePickerSlotProps(isDark, isDark ? drawerTheme.surface : '#ffffff'),
    [isDark, drawerTheme.surface],
  );
  const orderId = Number(order?.id || 0);
  const orderNumber = String(order?.order_number || order?.orderNumber || '').trim();
  const resolvedBookingId = Number(
    booking?.id || booking?.booking_id || order?.booking_id || order?.booking?.id || 0,
  ) || null;
  const bookingCode = booking?.code || booking?.booking_code || order?.booking_code || null;
  const guestName =
    booking?.guest_name ||
    booking?.guest?.name ||
    booking?.guest?.full_name ||
    order?.customer_name ||
    null;

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [summary, setSummary] = useState({
    total_amount: 0,
    total_paid: 0,
    due_amount: 0,
    payment_status: order?.payment_status || '',
  });
  const [receiveForm, setReceiveForm] = useState(emptyReceiveForm());
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [editDraft, setEditDraft] = useState({
    amount: '',
    payment_method: 'Cash',
    note: '',
    status: 'captured',
    collected_at: moment(),
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, payment: null });
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingPayment, setDeletingPayment] = useState(false);

  const modalContainer = useCallback(() => drawerRootRef.current || undefined, []);

  const closeDeleteDialog = useCallback(() => {
    if (deletingPayment) return;
    setDeleteDialog({ open: false, payment: null });
    setDeleteConfirmText('');
  }, [deletingPayment]);

  const openDeleteDialog = useCallback((payment) => {
    setDeleteConfirmText('');
    setDeleteDialog({ open: true, payment });
  }, []);

  const inputFieldSx = useMemo(
    () => ({
      '& .MuiOutlinedInput-root': {
        backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
      },
    }),
    [isDark],
  );

  const applyOrderSummary = useCallback((nextSummary, fallbackOrder = order) => {
    const totalAmount = Number(nextSummary?.total_amount ?? fallbackOrder?.total_amount ?? 0);
    const totalPaid = Number(nextSummary?.total_paid ?? fallbackOrder?.partial_paid_amount ?? 0);
    const dueAmount = Math.max(
      0,
      Number(
        nextSummary?.due_amount ??
          fallbackOrder?.due_amount ??
          Math.max(totalAmount - totalPaid, 0),
      ),
    );
    setSummary({
      total_amount: totalAmount,
      total_paid: totalPaid,
      due_amount: dueAmount,
      payment_status: nextSummary?.payment_status || fallbackOrder?.payment_status || '',
    });
    return { totalAmount, totalPaid, dueAmount };
  }, [order]);

  const syncSummaryFromPayments = useCallback(
    (paymentsList, fallbackOrder = order) => {
      const totalAmount = Number(fallbackOrder?.total_amount ?? 0);
      const totalPaid = sumCapturedPayments(paymentsList);
      const dueAmount = Math.max(0, totalAmount - totalPaid);
      return applyOrderSummary(
        {
          total_amount: totalAmount,
          total_paid: totalPaid,
          due_amount: dueAmount,
          payment_status: fallbackOrder?.payment_status || '',
        },
        fallbackOrder,
      );
    },
    [applyOrderSummary, order],
  );

  const fetchPayments = useCallback(async () => {
    if (!orderId) {
      setPayments([]);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const rows = await getPosOrderPayments({ order_id: orderId });
      const list = Array.isArray(rows) ? rows : [];
      setPayments(list);
      syncSummaryFromPayments(list, order);
    } catch (err) {
      setError(restaurantPaymentErrorMessage(err, 'Unable to load payment history.'));
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [order, orderId, syncSummaryFromPayments]);

  useEffect(() => {
    if (open && orderId) {
      fetchPayments();
    } else if (!open) {
      setPayments([]);
      setError('');
      setEditingPaymentId(null);
      setDeleteDialog({ open: false, payment: null });
      setReceiveForm(emptyReceiveForm());
      setDeleteConfirmText('');
      setInvoicePreviewOpen(false);
    }
  }, [fetchPayments, open, orderId]);

  useEffect(() => {
    if (!open || editingPaymentId) return;
    const { dueAmount } = syncSummaryFromPayments(payments, order);
    setReceiveForm((prev) => ({
      ...prev,
      amount: dueAmount > 0 ? formatAmountInput(dueAmount) : '',
      collected_at: prev.collected_at?.isValid?.() ? prev.collected_at : moment(),
    }));
  }, [editingPaymentId, open, order, payments, syncSummaryFromPayments]);

  const paymentBreakdownSummary = useMemo(() => {
    const totalAmount = round2(summary.total_amount ?? order?.total_amount ?? 0);
    // Prefer live payment history so Received Amount can exceed order total.
    const paidAmount =
      payments.length > 0
        ? sumCapturedPayments(payments)
        : round2(summary.total_paid ?? order?.partial_paid_amount ?? order?.paid_amount ?? 0);
    const remainingAmount = round2(Math.max(0, totalAmount - paidAmount));
    return buildRestaurantPaymentSummary(order, {
      total_amount: totalAmount,
      total_paid: paidAmount,
      due_amount: remainingAmount,
      payment_status: summary.payment_status,
    });
  }, [order, payments, summary]);

  const handleReceivePayment = async () => {
    const amount = parseAmount(receiveForm.amount);
    if (!orderId) return;
    if (amount <= 0) {
      setError('Enter a valid payment amount.');
      return;
    }
    if (!receiveForm.payment_method) {
      setError('Select a payment method.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const collectedAt = toPaymentProcessedAtIso(receiveForm.collected_at);
      const response = await createPosOrderPayment({
        order_id: orderId,
        amount,
        payment_method: receiveForm.payment_method,
        note: receiveForm.note || '',
        status: 'captured',
        ...(collectedAt ? { collected_at: collectedAt } : {}),
      });
      const nextPayment = response?.payment;
      if (nextPayment) {
        setPayments((prev) => [nextPayment, ...prev]);
      } else {
        await fetchPayments();
      }
      const nextSummary = applyOrderSummary(response?.order_summary, order);
      setReceiveForm(emptyReceiveForm(nextSummary.dueAmount > 0 ? nextSummary.dueAmount : ''));
      onPaymentsChanged?.(response?.order_summary);
      onClose?.();
    } catch (err) {
      setError(restaurantPaymentErrorMessage(err, 'Unable to record payment.'));
    } finally {
      setSubmitting(false);
    }
  };

  const startEditPayment = (payment) => {
    setEditingPaymentId(payment.id);
    setEditDraft({
      amount: formatAmountInput(payment.amount),
      payment_method: payment.payment_method || 'Cash',
      note: payment.note || '',
      status: payment.status || 'captured',
      collected_at: toMomentValue(payment.collected_at),
    });
    setError('');
  };

  const cancelEditPayment = () => {
    setEditingPaymentId(null);
    setEditDraft({
      amount: '',
      payment_method: 'Cash',
      note: '',
      status: 'captured',
      collected_at: moment(),
    });
  };

  const saveEditPayment = async () => {
    const amount = parseAmount(editDraft.amount);
    if (!editingPaymentId || amount <= 0) {
      setError('Enter a valid payment amount.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const collectedAt = toPaymentProcessedAtIso(editDraft.collected_at);
      const response = await updatePosOrderPayment(editingPaymentId, {
        amount,
        payment_method: editDraft.payment_method,
        note: editDraft.note || '',
        status: editDraft.status,
        ...(collectedAt ? { collected_at: collectedAt } : {}),
      });
      const updatedPayment = response?.payment;
      if (updatedPayment) {
        setPayments((prev) => prev.map((item) => (item.id === updatedPayment.id ? updatedPayment : item)));
      } else {
        await fetchPayments();
      }
      applyOrderSummary(response?.order_summary, order);
      cancelEditPayment();
      onPaymentsChanged?.(response?.order_summary);
    } catch (err) {
      setError(restaurantPaymentErrorMessage(err, 'Unable to update payment.'));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeletePayment = async () => {
    const paymentId = deleteDialog.payment?.id;
    if (!paymentId) return;
    if (deleteConfirmText.trim().toLowerCase() !== DELETE_CONFIRM_WORD) return;

    setDeletingPayment(true);
    setError('');
    try {
      const response = await deletePosOrderPayment(paymentId);
      setPayments((prev) => prev.filter((item) => item.id !== paymentId));
      const nextSummary = applyOrderSummary(response?.order_summary, order);
      setReceiveForm(emptyReceiveForm(nextSummary.dueAmount > 0 ? nextSummary.dueAmount : ''));
      setDeleteDialog({ open: false, payment: null });
      setDeleteConfirmText('');
      onPaymentsChanged?.(response?.order_summary);
    } catch (err) {
      setError(restaurantPaymentErrorMessage(err, 'Unable to delete payment.'));
    } finally {
      setDeletingPayment(false);
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: {
            ...getBookingDrawerPaperSx(isDark),
            width: BOOKING_DRAWER_WIDTH_SX,
            zIndex: (theme) => theme.zIndex.drawer + 2,
          },
        }}
      >
        <Box
          ref={drawerRootRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 1.5,
              pt: 1.5,
              pb: 1.85,
              ...getBookingDrawerHeaderSx(isDark),
              background: isDark
                ? `linear-gradient(140deg, ${alpha(ACCENT, 0.12)} 0%, ${drawerTheme.headerBg} 100%)`
                : `linear-gradient(140deg, ${alpha(ACCENT, 0.12)} 0%, #ffffff 100%)`,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flexWrap: 'wrap' }} useFlexGap>
                <PaymentIcon sx={{ color: ACCENT, fontSize: 22, flexShrink: 0 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', color: drawerTheme.text }}>
                  Payment Collection
                </Typography>
                {orderNumber ? (
                  <Chip
                    size="small"
                    clickable={Boolean(resolvedBookingId || orderId)}
                    label={`Order ${orderNumber}`}
                    onClick={() => {
                      if (resolvedBookingId || orderId) {
                        setInvoicePreviewOpen(true);
                      }
                    }}
                    title={
                      resolvedBookingId
                        ? 'Open combined restaurant invoice for this booking'
                        : orderId
                          ? 'Open restaurant invoice'
                          : undefined
                    }
                    sx={{
                      height: 24,
                      fontWeight: 700,
                      bgcolor: isDark ? 'rgba(76,225,182,0.12)' : 'rgba(20,184,166,0.1)',
                      color: ACCENT,
                      border: `1px solid ${alpha(ACCENT, 0.35)}`,
                      cursor: resolvedBookingId || orderId ? 'pointer' : 'default',
                      '&:hover': {
                        bgcolor: isDark ? 'rgba(76,225,182,0.2)' : 'rgba(20,184,166,0.18)',
                        filter: 'brightness(0.98)',
                      },
                    }}
                  />
                ) : null}
              </Stack>
              <IconButton size="small" onClick={onClose} sx={{ color: drawerTheme.subtleText, flexShrink: 0 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper elevation={0} sx={getDrawerSectionPaperSx(isDark, drawerTheme)}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: drawerTheme.text }}>
                  Payment Summary
                </Typography>
                {paymentBreakdownSummary.gstIncluded || paymentBreakdownSummary.gstAmount > 0 ? (
                  <BookingGstModeChip inclusive={false} isDark={isDark} />
                ) : null}
              </Box>
              <BookingPaymentBreakdown
                summary={paymentBreakdownSummary}
                isDark={isDark}
                drawerTheme={drawerTheme}
                allowExtraChargesEdit={false}
                detailLabel="order total"
              />
            </Paper>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75, color: drawerTheme.text }}>
                Payment History
                {payments.length > 0 ? (
                  <Chip label={payments.length} size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                ) : null}
              </Typography>

              {error ? (
                <Alert
                  severity={error === RESTAURANT_PAYMENT_ACCESS_MESSAGE ? 'warning' : 'error'}
                  sx={{ mb: 1.5 }}
                  onClose={() => setError('')}
                >
                  {error}
                </Alert>
              ) : null}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} sx={{ color: ACCENT }} />
                </Box>
              ) : payments.length === 0 ? (
                <Typography variant="body2" sx={{ color: drawerTheme.subtleText, textAlign: 'center', py: 3 }}>
                  No payment history found
                </Typography>
              ) : (
                <Stack spacing={0.75}>
                  {payments.map((payment, index) => {
                    const statusColors = getPaymentStatusColors(payment.status, isDark);
                    const isEditing = editingPaymentId === payment.id;

                    return (
                      <Fade in key={payment.id || index} timeout={280}>
                        <Paper
                          elevation={0}
                          sx={{
                            px: 1,
                            py: 0.85,
                            borderRadius: '10px',
                            backgroundColor: isEditing
                              ? (isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.08)')
                              : (isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'),
                            border: `1px solid ${isEditing ? ACCENT : (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)')}`,
                            boxShadow: isEditing ? `0 0 0 1px ${alpha(ACCENT, 0.25)}` : 'none',
                            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 0.75,
                              minHeight: 28,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: drawerTheme.text, fontSize: '0.86rem', lineHeight: 1.2 }}
                            >
                              {formatCurrency(payment.amount)}
                            </Typography>
                            <Stack direction="row" spacing={0.45} alignItems="center" flexWrap="wrap" useFlexGap justifyContent="flex-end">
                              <Chip
                                label={statusColors.label}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  backgroundColor: statusColors.bg,
                                  color: statusColors.color,
                                  fontWeight: 600,
                                  border: `1px solid ${statusColors.color}55`,
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                              {payment.payment_method ? (
                                <Chip
                                  icon={<PaymentIcon sx={{ fontSize: 11 }} />}
                                  label={payment.payment_method}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                                    color: ACCENT,
                                    fontWeight: 600,
                                    border: `1px solid ${alpha(ACCENT, 0.22)}`,
                                    '& .MuiChip-label': { px: 0.65 },
                                    '& .MuiChip-icon': { ml: 0.45 },
                                  }}
                                />
                              ) : null}
                            </Stack>
                          </Box>

                          {!isEditing ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 0.75,
                                mt: 1,
                                pt: 0.75,
                                borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                              }}
                            >
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                {payment.collected_at ? (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: drawerTheme.subtleText, fontSize: '0.68rem', lineHeight: 1.2, display: 'block' }}
                                  >
                                    {formatHotelDateTime(payment.collected_at)}
                                  </Typography>
                                ) : null}
                                {payment.note ? (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: drawerTheme.subtleText,
                                      fontSize: '0.66rem',
                                      lineHeight: 1.25,
                                      display: 'block',
                                      mt: payment.collected_at ? 0.2 : 0,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {payment.note}
                                  </Typography>
                                ) : null}
                              </Box>
                              <Stack direction="row" spacing={0.35} sx={{ flexShrink: 0 }}>
                                <Tooltip title="Edit payment">
                                  <IconButton
                                    size="small"
                                    onClick={() => startEditPayment(payment)}
                                    sx={{
                                      p: 0.45,
                                      color: ACCENT,
                                      border: `1px solid ${alpha(ACCENT, 0.35)}`,
                                      borderRadius: '7px',
                                    }}
                                  >
                                    <EditOutlinedIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete payment">
                                  <IconButton
                                    size="small"
                                    onClick={() => openDeleteDialog(payment)}
                                    sx={{
                                      p: 0.45,
                                      color: '#f44336',
                                      border: '1px solid rgba(244,67,54,0.3)',
                                      borderRadius: '7px',
                                    }}
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Box>
                          ) : null}

                          <Collapse in={isEditing} timeout={300} unmountOnExit>
                            <Box
                              sx={{
                                mt: 1.25,
                                pt: 1.25,
                                borderTop: `1px dashed ${isDark ? 'rgba(76, 225, 182, 0.25)' : 'rgba(76, 225, 182, 0.35)'}`,
                              }}
                            >
                              <Typography variant="caption" sx={{ color: ACCENT, fontWeight: 700, letterSpacing: 0.4, mb: 1.25, display: 'block' }}>
                                EDIT PAYMENT
                              </Typography>
                              <Stack spacing={1.25}>
                                <Grid container spacing={1.25}>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      label="Amount"
                                      type="number"
                                      size="small"
                                      fullWidth
                                      value={editDraft.amount}
                                      onChange={(event) => setEditDraft((prev) => ({ ...prev, amount: event.target.value }))}
                                      onBlur={() => {
                                        setEditDraft((prev) => ({
                                          ...prev,
                                          amount: formatAmountInput(prev.amount),
                                        }));
                                      }}
                                      InputProps={{
                                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                      }}
                                      inputProps={{ min: 0.01, step: 0.01 }}
                                      sx={inputFieldSx}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                      <DateTimePicker
                                        label="Payment Date & Time"
                                        disableFuture
                                        value={editDraft.collected_at}
                                        onChange={(value) => setEditDraft((prev) => ({ ...prev, collected_at: value }))}
                                        slotProps={{
                                          ...datePickerSlotProps,
                                          textField: {
                                            fullWidth: true,
                                            size: 'small',
                                            sx: inputFieldSx,
                                          },
                                        }}
                                      />
                                    </LocalizationProvider>
                                  </Grid>
                                </Grid>
                                <PaymentMethodChipSelector
                                  value={editDraft.payment_method}
                                  onChange={(method) => setEditDraft((prev) => ({ ...prev, payment_method: method }))}
                                  isDark={isDark}
                                />
                                <PaymentStatusChipSelector
                                  value={editDraft.status}
                                  onChange={(status) => setEditDraft((prev) => ({ ...prev, status }))}
                                  isDark={isDark}
                                />
                                <TextField
                                  label="Note"
                                  size="small"
                                  fullWidth
                                  multiline
                                  minRows={2}
                                  value={editDraft.note}
                                  onChange={(event) => setEditDraft((prev) => ({ ...prev, note: event.target.value }))}
                                  sx={inputFieldSx}
                                />
                                <Stack direction="row" spacing={1}>
                                  <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={cancelEditPayment}
                                    disabled={submitting}
                                    sx={getBookingDrawerOutlinedButtonSx(isDark)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={saveEditPayment}
                                    disabled={submitting}
                                    sx={getBookingDrawerPrimaryButtonSx(isDark)}
                                  >
                                    {submitting ? 'Saving...' : 'Save Changes'}
                                  </Button>
                                </Stack>
                              </Stack>
                            </Box>
                          </Collapse>
                        </Paper>
                      </Fade>
                    );
                  })}
                </Stack>
              )}
            </Box>

            <Paper elevation={0} sx={getDrawerSectionPaperSx(isDark, drawerTheme)}>
              <Typography variant="subtitle2" sx={{ color: drawerTheme.text, fontWeight: 600, mb: 1.5 }}>
                Collect Payment
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Payment Amount"
                    type="number"
                    value={receiveForm.amount}
                    onChange={(event) => setReceiveForm((prev) => ({ ...prev, amount: event.target.value }))}
                    onBlur={() => {
                      setReceiveForm((prev) => ({
                        ...prev,
                        amount: formatAmountInput(prev.amount),
                      }));
                    }}
                    inputProps={{ step: '0.01', min: '0' }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    sx={inputFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateTimePicker
                      label="Payment Date & Time"
                      disableFuture
                      value={receiveForm.collected_at}
                      onChange={(value) => setReceiveForm((prev) => ({ ...prev, collected_at: value }))}
                      slotProps={{
                        ...datePickerSlotProps,
                        textField: {
                          fullWidth: true,
                          sx: inputFieldSx,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                  <PaymentMethodChipSelector
                    value={receiveForm.payment_method}
                    onChange={(method) => setReceiveForm((prev) => ({ ...prev, payment_method: method }))}
                    isDark={isDark}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Note (Optional)"
                    value={receiveForm.note}
                    onChange={(event) => setReceiveForm((prev) => ({ ...prev, note: event.target.value }))}
                    multiline
                    minRows={2}
                    sx={inputFieldSx}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Box>

          <Box sx={{ p: 1.5, ...getBookingDrawerFooterSx(isDark) }}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                fullWidth
                onClick={onClose}
                disabled={submitting}
                sx={getBookingDrawerOutlinedButtonSx(isDark)}
              >
                Close
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={handleReceivePayment}
                disabled={submitting || !orderId}
                sx={getBookingDrawerPrimaryButtonSx(isDark)}
              >
                {submitting ? 'Saving...' : 'Receive Payment'}
              </Button>
            </Stack>
          </Box>

          <DeletePaymentConfirmDialog
            open={deleteDialog.open}
            payment={deleteDialog.payment}
            isDark={isDark}
            drawerTheme={drawerTheme}
            modalContainer={modalContainer}
            confirmText={deleteConfirmText}
            onConfirmTextChange={setDeleteConfirmText}
            onClose={closeDeleteDialog}
            onConfirm={confirmDeletePayment}
            deleting={deletingPayment}
          />

          {resolvedBookingId ? (
            <BookingInvoicePreviewDialog
              open={invoicePreviewOpen}
              onClose={() => setInvoicePreviewOpen(false)}
              bookingId={resolvedBookingId}
              invoiceNumber={
                booking?.invoice_number ||
                (bookingCode ? `Restaurant · ${bookingCode}` : `Restaurant · Order ${orderNumber}`)
              }
              bookingCode={bookingCode}
              guestName={guestName}
              isDark={isDark}
              accentColor={ACCENT}
              invoiceScope="restaurant"
            />
          ) : (
            <BookingInvoicePreviewDialog
              open={invoicePreviewOpen}
              onClose={() => setInvoicePreviewOpen(false)}
              bookingId={orderId}
              invoiceNumber={orderNumber ? `Restaurant Invoice ${orderNumber}` : 'Restaurant Invoice'}
              bookingCode={orderNumber || String(orderId || '')}
              guestName={guestName || 'Restaurant Guest'}
              isDark={isDark}
              accentColor={ACCENT}
              invoiceScope="restaurant"
              fetchOfficialPdfBlob={fetchPosOrderInvoicePdfBlob}
              downloadOfficialInvoice={(id) => downloadPosOrderInvoice(id, orderNumber)}
              fetchDraftPdfBlob={fetchPosOrderDraftInvoicePdfBlob}
              fetchDraftDefaults={fetchPosOrderDraftInvoiceDefaults}
            />
          )}
        </Box>
      </Drawer>
    </>
  );
}
